import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, Paperclip, X, Loader2, Send } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLang } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  'technical_issue',
  'review_issue',
  'wallet_issue',
  'business_issue',
  'account_issue',
  'suggestion',
  'partnership',
  'other',
];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MIN_MESSAGE_LEN = 10;
const MAX_MESSAGE_LEN = 5000;

export default function SupportDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null); // { name, type, size, base64 }
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setReason(''); setMessage(''); setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_IMAGE_TYPES.includes(f.type)) {
      toast.error(t('sup_err_type'));
      e.target.value = '';
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      toast.error(t('sup_err_size'));
      e.target.value = '';
      return;
    }
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const s = r.result || '';
        const comma = s.indexOf(',');
        resolve(comma >= 0 ? s.slice(comma + 1) : s);
      };
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    setFile({ name: f.name, type: f.type, size: f.size, base64: b64 });
  };

  const onSubmit = async () => {
    if (!reason) { toast.error(t('sup_err_reason')); return; }
    const msg = (message || '').trim();
    if (msg.length < MIN_MESSAGE_LEN) { toast.error(t('sup_err_message_short')); return; }
    if (msg.length > MAX_MESSAGE_LEN) { toast.error(t('sup_err_message_long')); return; }
    setSending(true);
    try {
      const payload = { reason, message: msg };
      if (file) {
        payload.attachment_base64 = file.base64;
        payload.attachment_filename = file.name;
        payload.attachment_content_type = file.type;
      }
      await axios.post(`${API}/support/tickets`, payload, { withCredentials: true });
      toast.success(t('sup_sent'));
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('sup_err_generic'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending) { if (!v) reset(); onOpenChange(v); } }}>
      <DialogContent className="sm:max-w-xl" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <LifeBuoy className="w-5 h-5" strokeWidth={1.5} /> {t('sup_title')}
          </DialogTitle>
          <DialogDescription className="font-sans">{t('sup_subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="sup-reason" className="font-sans">{t('sup_reason_label')}</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="sup-reason" data-testid="sup-reason" className="mt-1">
                <SelectValue placeholder={t('sup_reason_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map(r => (
                  <SelectItem key={r} value={r} data-testid={`sup-reason-${r}`}>
                    {t(`sup_reason_${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <Label htmlFor="sup-message" className="font-sans">{t('sup_message_label')}</Label>
              <span className="text-[11px] text-muted-foreground font-sans">
                {message.length}/{MAX_MESSAGE_LEN}
              </span>
            </div>
            <Textarea
              id="sup-message"
              data-testid="sup-message"
              rows={6}
              placeholder={t('sup_message_placeholder')}
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={MAX_MESSAGE_LEN}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="font-sans">{t('sup_attachment_label')}</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onPickFile}
                className="hidden"
                data-testid="sup-attachment-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
                data-testid="sup-attachment-pick"
              >
                <Paperclip className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                {file ? t('sup_attachment_change') : t('sup_attachment_pick')}
              </Button>
              {file && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground font-sans" data-testid="sup-attachment-preview">
                  <span className="truncate max-w-[200px]">{file.name}</span>
                  <span>· {(file.size / 1024).toFixed(0)} KB</span>
                  <button
                    type="button"
                    onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ''; }}
                    className="p-1 rounded hover:bg-secondary"
                    aria-label="remove attachment"
                    data-testid="sup-attachment-remove"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground font-sans mt-1">{t('sup_attachment_hint')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }} disabled={sending} data-testid="sup-cancel">
            {t('sup_cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={sending} data-testid="sup-submit" className="btn-lift">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {t('sup_submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
